/**
 * Shared CSS keyframes + gooey SVG filters.
 * Injected once into document.head / document.body on first mount.
 */
import React, { useEffect } from 'react';

export const DEFAULT_COLOR = 'rgba(208, 188, 26, 0.5)';

const STYLE_ID = '__organic_loaders_style';
const DEFS_ID  = '__organic_loaders_defs';

const CSS = `
  .ol-cell { width: 100%; height: 100%; display: grid; place-items: center; position: relative; }

  /* 01 morphing blob */
  .ol-01 { width: 55%; height: 55%; background: var(--ol-c);
    animation: ol01-morph 4s ease-in-out infinite, ol-spin 9s linear infinite; }
  @keyframes ol01-morph {
    0%,100% { border-radius: 62% 38% 54% 46% / 48% 56% 44% 52%; }
    25%     { border-radius: 40% 60% 38% 62% / 60% 40% 60% 40%; }
    50%     { border-radius: 55% 45% 70% 30% / 35% 65% 35% 65%; }
    75%     { border-radius: 30% 70% 50% 50% / 55% 40% 60% 45%; }
  }

  /* 02 breathing dots */
  .ol-02 { display: flex; gap: 10px; align-items: center; }
  .ol-02 span { width: 22px; height: 22px; background: var(--ol-c); border-radius: 50%;
    animation: ol02-breathe 1.4s ease-in-out infinite; }
  .ol-02 span:nth-child(2){animation-delay:.18s}
  .ol-02 span:nth-child(3){animation-delay:.36s}
  .ol-02 span:nth-child(4){animation-delay:.54s}
  @keyframes ol02-breathe {
    0%,80%,100% { transform: scale(.45); }
    40% { transform: scale(1.15); border-radius: 45% 55% 55% 45% / 55% 45% 55% 45%; }
  }

  /* 03 gooey orbit */
  .ol-03 { width: 70%; height: 70%; position: relative; filter: url(#ol-goo); }
  .ol-03 .a, .ol-03 .b { position: absolute; left: 50%; top: 50%;
    width: 46px; height: 46px; margin: -23px 0 0 -23px;
    background: var(--ol-c); border-radius: 50%; }
  .ol-03 .a { animation: ol03-orbit 2.2s linear infinite; }
  .ol-03 .b { animation: ol03-orbit 2.2s linear infinite reverse; }
  @keyframes ol03-orbit {
    0%   { transform: rotate(0deg)   translateX(34px) scale(1); }
    50%  { transform: rotate(180deg) translateX(34px) scale(.7); }
    100% { transform: rotate(360deg) translateX(34px) scale(1); }
  }

  /* 04 drip */
  .ol-04 { width: 35%; height: 65%; position: relative; filter: url(#ol-goo); }
  .ol-04 .head { position: absolute; top: 0; left: 50%; transform: translateX(-50%);
    width: 50px; height: 50px; border-radius: 50%; background: var(--ol-c); }
  .ol-04 .drop { position: absolute; top: 28px; left: 50%; margin-left: -10px;
    width: 20px; height: 20px; border-radius: 50%; background: var(--ol-c);
    animation: ol04-drop 1.8s ease-in infinite; }
  @keyframes ol04-drop {
    0%   { transform: translateY(0) scale(.6); opacity: 1; }
    60%  { transform: translateY(60px) scale(1); }
    85%  { transform: translateY(80px) scale(.4); opacity: 1; }
    100% { transform: translateY(90px) scale(0); opacity: 0; }
  }

  /* 05 wavy ring */
  .ol-05 { width: 55%; height: 55%; border-radius: 50%;
    background: radial-gradient(closest-side, transparent calc(50% - 10px),
                                var(--ol-c) calc(50% - 9px) calc(50% - 1px), transparent 50%);
    animation: ol05-wob 5s ease-in-out infinite, ol-spin 6s linear infinite; }
  @keyframes ol05-wob {
    0%,100% { border-radius: 50%; }
    25%     { border-radius: 60% 40% 55% 45% / 45% 55% 45% 55%; }
    50%     { border-radius: 42% 58% 38% 62% / 60% 40% 60% 40%; }
    75%     { border-radius: 55% 45% 65% 35% / 35% 65% 35% 65%; }
  }

  /* 06 amoeba pulse */
  .ol-06 { width: 65%; height: 65%; background: var(--ol-c);
    animation: ol06-pulse 2.4s ease-in-out infinite; }
  @keyframes ol06-pulse {
    0%,100% { border-radius: 50%; transform: scale(.6); }
    50%     { border-radius: 38% 62% 55% 45% / 60% 38% 62% 40%; transform: scale(1); }
  }

  /* 07 gooey trail dots */
  .ol-07 { width: 75%; height: 20%; position: relative; filter: url(#ol-goo); }
  .ol-07 span { position: absolute; top: 50%; left: 0;
    width: 28px; height: 28px; margin-top: -14px;
    background: var(--ol-c); border-radius: 50%;
    animation: ol07-slide 1.8s ease-in-out infinite; }
  .ol-07 span:nth-child(2){animation-delay:.15s}
  .ol-07 span:nth-child(3){animation-delay:.3s}
  @keyframes ol07-slide {
    0%,100% { transform: translateX(0) scale(.6); }
    50%     { transform: translateX(120px) scale(1); }
  }

  /* 08 ink beat */
  .ol-08 { width: 45%; height: 45%; background: var(--ol-c);
    animation: ol08-beat 1.6s cubic-bezier(.6,0,.4,1) infinite; }
  @keyframes ol08-beat {
    0%,100% { border-radius: 50%; transform: scale(.7); }
    20%     { border-radius: 60% 40% 40% 60% / 40% 60% 40% 60%; transform: scale(1.05); }
    40%     { border-radius: 40% 60% 60% 40% / 60% 40% 60% 40%; transform: scale(.85); }
    60%     { border-radius: 55% 45% 45% 55% / 50% 55% 45% 50%; transform: scale(1.0); }
  }

  /* 09 cell division */
  .ol-09 { width: 80%; height: 40%; position: relative; filter: url(#ol-goo); }
  .ol-09 .c { position: absolute; top: 50%; left: 50%;
    width: 56px; height: 56px; margin: -28px 0 0 -28px;
    background: var(--ol-c); border-radius: 50%; }
  .ol-09 .c1 { animation: ol09-left 2.4s ease-in-out infinite; }
  .ol-09 .c2 { animation: ol09-right 2.4s ease-in-out infinite; }
  @keyframes ol09-left  { 0%,100%{transform:translateX(0) scale(1)} 50%{transform:translateX(-36px) scale(.78)} }
  @keyframes ol09-right { 0%,100%{transform:translateX(0) scale(1)} 50%{transform:translateX( 36px) scale(.78)} }

  @keyframes ol-spin { to { transform: rotate(360deg); } }
`;

const DEFS_HTML = `
  <svg width="0" height="0">
    <defs>
      <filter id="ol-goo">
        <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur"/>
        <feColorMatrix in="blur" mode="matrix"
          values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -11" result="goo"/>
        <feComposite in="SourceGraphic" in2="goo" operator="atop"/>
      </filter>
      <filter id="ol-goo-strong">
        <feGaussianBlur in="SourceGraphic" stdDeviation="8"/>
        <feColorMatrix mode="matrix"
          values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 26 -13"/>
      </filter>
    </defs>
  </svg>
`;

export function useOrganicLoadersAssets() {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!document.getElementById(STYLE_ID)) {
      const s = document.createElement('style');
      s.id = STYLE_ID;
      s.textContent = CSS;
      document.head.appendChild(s);
    }
    if (!document.getElementById(DEFS_ID)) {
      const wrap = document.createElement('div');
      wrap.id = DEFS_ID;
      wrap.setAttribute('aria-hidden', 'true');
      wrap.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none';
      wrap.innerHTML = DEFS_HTML;
      document.body.appendChild(wrap);
    }
  }, []);
}

/**
 * Shared cell wrapper for CSS-based loaders.
 * Sets the --ol-c custom property so the inner CSS picks up the color.
 */
export function Cell({ size = 200, color = DEFAULT_COLOR, children, style }) {
  useOrganicLoadersAssets();
  return (
    <div
      className="ol-cell"
      role="status"
      aria-label="Loading"
      style={{ width: size, height: size, '--ol-c': color, ...style }}
    >
      {children}
    </div>
  );
}
